import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '@/lib/axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import SignaturePad from '@/components/SignaturePad';
import WorkerNav from '@/components/WorkerNav';
import { ClipboardCheck, AlertTriangle, CheckCircle2, MapPin, Loader2, ShieldAlert, HardHat, Hospital } from 'lucide-react';

const ProjectJHACard = ({ jha, onReview }) => (
  <Card
    className={`border-l-4 ${jha.acknowledgmentRequired ? 'border-l-red-500' : 'border-l-green-500'}`}
    data-testid={`project-jha-card-${jha.id}`}
  >
    <CardHeader className="pb-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <CardTitle className="text-base truncate">{jha.projectName}</CardTitle>
          {jha.projectLocation && (
            <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{jha.projectLocation}</span>
            </p>
          )}
        </div>
        {jha.acknowledgmentRequired ? (
          <Badge className="bg-red-100 text-red-700 border border-red-200 shrink-0">Sign required</Badge>
        ) : (
          <Badge className="bg-green-100 text-green-700 border border-green-200 shrink-0">Signed</Badge>
        )}
      </div>
    </CardHeader>
    <CardContent className="space-y-3">
      <p className="text-xs text-gray-500">
        Revision {jha.approvedRevision} · approved by {jha.approvedByName || 'management'}
        {jha.approvedAt ? ` on ${jha.approvedAt.slice(0, 10)}` : ''}
      </p>
      {jha.previouslyAcknowledgedRevision && jha.acknowledgmentRequired && (
        <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
          This JHA changed since you signed revision {jha.previouslyAcknowledgedRevision}. Review it again before you work.
        </p>
      )}
      <Button
        className="w-full"
        variant={jha.acknowledgmentRequired ? 'default' : 'outline'}
        onClick={() => onReview(jha)}
        data-testid={`review-jha-btn-${jha.id}`}
      >
        <ClipboardCheck className="w-4 h-4 mr-2" />
        {jha.acknowledgmentRequired ? 'Review & sign' : 'View'}
      </Button>
    </CardContent>
  </Card>
);

const WorkerProjectJHAs = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [jhas, setJhas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [signature, setSignature] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/worker/pre-construction-jhas');
      setJhas(res.data?.jhas || []);
    } catch (error) {
      toast.error('Could not load project JHAs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSign = async () => {
    if (!signature || !selected) return;
    setSaving(true);
    try {
      await axiosInstance.post(`/pre-construction-jha/${selected.id}/crew-signon`, { signature });
      toast.success(`Signed revision ${selected.approvedRevision}`);
      setSelected(null);
      setSignature(null);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not record your signature');
    } finally {
      setSaving(false);
    }
  };

  const pending = jhas.filter(j => j.acknowledgmentRequired);
  const ep = selected?.emergencyPlan || {};
  const sitePlan = ep.siteEmergencyPlan || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <WorkerNav user={user} onLogout={onLogout} />
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Project JHAs</h1>
          <p className="text-sm text-gray-600 mt-1">
            Review and sign the approved job hazard analysis for your project before you start work.
          </p>
        </div>

        {pending.length > 0 && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="pending-signon-banner">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <p className="text-sm text-red-800">
              {pending.length} project JHA{pending.length > 1 ? 's need' : ' needs'} your signature before you work.
            </p>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
        ) : jhas.length === 0 ? (
          <Card><CardContent className="py-10 text-center text-gray-500" data-testid="no-project-jhas">
            No approved project JHAs for you right now.
          </CardContent></Card>
        ) : (
          <div className="space-y-3" data-testid="project-jha-list">
            {jhas.map(jha => <ProjectJHACard key={jha.id} jha={jha} onReview={setSelected} />)}
          </div>
        )}
      </div>

      <Dialog open={!!selected} onOpenChange={(open) => { if (!open) { setSelected(null); setSignature(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="worker-jha-review-dialog">
          <DialogHeader>
            <DialogTitle>{selected?.projectName}</DialogTitle>
            <DialogDescription>
              Revision {selected?.approvedRevision} · approved by {selected?.approvedByName || 'management'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-sm">
            {selected?.scopeOfWork && (
              <div>
                <p className="font-semibold text-gray-900">Scope of work</p>
                <p className="text-gray-700">{selected.scopeOfWork}</p>
              </div>
            )}

            {(selected?.siteHazards || []).length > 0 && (
              <div>
                <p className="font-semibold text-gray-900 flex items-center gap-1.5">
                  <ShieldAlert className="w-4 h-4 text-amber-600" /> Site hazards &amp; controls
                </p>
                <ul className="mt-1 space-y-1.5" data-testid="worker-jha-hazards">
                  {selected.siteHazards.map((h, i) => (
                    <li key={h._rowId || `haz-${i}`} className="bg-amber-50 border border-amber-200 rounded p-2">
                      <span className="font-medium">{h.hazardType}</span>
                      {h.description ? ` — ${h.description}` : ''}
                      {h.controlMeasures && <div className="text-gray-700 mt-0.5">Control: {h.controlMeasures}</div>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {(selected?.ppeRequirements || []).length > 0 && (
              <div>
                <p className="font-semibold text-gray-900 flex items-center gap-1.5">
                  <HardHat className="w-4 h-4 text-pink-600" /> Required PPE
                </p>
                <ul className="mt-1 space-y-1">
                  {selected.ppeRequirements.map((p, i) => (
                    <li key={p._rowId || `ppe-${i}`} className="text-gray-700">
                      {p.workArea}: {Array.isArray(p.requiredPPE) ? p.requiredPPE.join(', ') : p.requiredPPE}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <p className="font-semibold text-gray-900 flex items-center gap-1.5">
                <Hospital className="w-4 h-4 text-red-600" /> Emergency information
              </p>
              {sitePlan.applies ? (
                <div className="text-gray-700 mt-1 bg-green-50 border border-green-200 rounded p-2">
                  <div className="font-medium">{sitePlan.planName}</div>
                  {sitePlan.planRevision && <div className="text-xs text-gray-600">{sitePlan.planRevision}</div>}
                  {sitePlan.accessLocation && <div className="mt-1">Where to find it: {sitePlan.accessLocation}</div>}
                </div>
              ) : (
                <div className="text-gray-700 mt-1 space-y-0.5">
                  <div>Hospital: {ep.nearestHospital || '—'}</div>
                  <div>{ep.hospitalAddress}</div>
                  <div>Phone: {ep.hospitalPhone || '—'}</div>
                  {(ep.assemblyPoints || []).filter(Boolean).length > 0 && (
                    <div>Assembly: {ep.assemblyPoints.filter(Boolean).join(', ')}</div>
                  )}
                </div>
              )}
            </div>

            {selected?.acknowledgmentRequired ? (
              <div>
                <p className="font-semibold text-gray-900">Sign to confirm you reviewed this JHA</p>
                <SignaturePad value={signature} onChange={setSignature} height={160} />
              </div>
            ) : (
              <p className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded p-2">
                <CheckCircle2 className="w-4 h-4" /> You already signed this revision.
              </p>
            )}
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => { setSelected(null); setSignature(null); }} data-testid="worker-jha-close-btn">
              Close
            </Button>
            {selected?.acknowledgmentRequired && (
              <Button onClick={handleSign} disabled={!signature || saving} data-testid="worker-jha-sign-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ClipboardCheck className="w-4 h-4 mr-1" />}
                {saving ? 'Saving...' : 'Sign & acknowledge'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkerProjectJHAs;
